import { useState, useEffect, useRef } from 'react';

export type UseOnlineOptions = {
  /** Fires when the browser reports `online` (e.g. brief “back online” toast). */
  onBecomeOnline?: () => void;
};

/**
 * Subscribes to `window` online/offline events (roadmap §11.1).
 * Prefer this over duplicating listeners in `App.tsx`.
 */
export function useOnline(options?: UseOnlineOptions): boolean {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const onBecomeOnlineRef = useRef(options?.onBecomeOnline);
  onBecomeOnlineRef.current = options?.onBecomeOnline;

  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      onBecomeOnlineRef.current?.();
    };
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  return online;
}
